/**
 * Feed Composer program hook — effect runner.
 *
 * Elm-ish architecture: this is the ONLY place that combines:
 * - useState/useRef for state management
 * - dispatch for message handling
 * - update() for pure state transitions
 * - runEffect() for side effects (outside the reducer)
 *
 * The reducer is PURE — it only returns new model + effects.
 * Effects are drained SYNCHRONOUSLY inside the dispatch wrapper,
 * not via useEffect (CORRECTION02k; see file-level design note
 * inside the hook for the full ownership contract).
 *
 * This hook bridges the pure Elm model to React's rendering.
 *
 * CORRECTION01 changes:
 * - R4: Dependencies injected from props
 * - R7: AbortController for effect cancellation on unmount/replace/departure
 *
 * CORRECTION02k:
 * - Synchronous effect drain inside dispatch wrapper
 * - Per-occurrence AbortController, per-instance controller set
 * - Reducer (update.ts) is the sole dedup authority
 *
 * CORRECTION02k-CORRECTION02 — Dependency-replacement LIVENESS:
 *
 *   Each in-flight logical effect occurrence is tracked in a
 *   per-instance ref-backed registry (`ownedRef`) keyed by a
 *   stable logical key (e.g. "LoadAuthor" or "SubmitPost:42").
 *   The registry holds:
 *
 *     OwnedOccurrence {
 *       effect: ComposerEffect,
 *       controller: AbortController, // current physical execution
 *     }
 *
 *   On dependency change, every in-flight occurrence is:
 *     1. aborted (defense in depth; the OLD runEffect settles
 *        via signal.aborted, suppressing any stale dispatch
 *        in effects.ts post-resolve / post-reject guards).
 *     2. re-launched against the NEW dependencies with a fresh
 *        AbortController. The new controller replaces the
 *        old one in the registry atomically.
 *
 *   Ownership cleanup is per-controller: the .finally of a
 *   given physical execution deletes that controller from the
 *   per-instance set AND, only if it is still the CURRENT
 *   controller for its key, deletes the registry entry. Stale
 *   finalizers cannot clear the registry entry of a replacement
 *   execution because the registry is checked at finalize time.
 *
 *   This registry is NOT React state. It is a useRef mutated
 *   only from event-time / effect-time code, never from the
 *   render body. `state.pendingEffects` is NOT reintroduced.
 *   `flushSync` / `setTimeout` / `queueMicrotask` are NOT used.
 *
 *   Caller contract: `dependencies` reference must be stable
 *   across renders that don't change the actual adapter
 *   (FeedComposePage meets this via
 *    useMemo([registrationKind])).
 *
 *   The OLD ABORT_ONLY contract from CORRECTION01 is
 *   intentionally superseded: a dep change that left the
 *   program stuck in 'loading' (because Started is gated by
 *   status===idle) was a state-machine dead end. This
 *   correction restores both the abort half and the re-issue
 *   half of the historical contract without re-introducing
 *   render-state effect delivery.
 */
import { useEffect, useCallback, useRef, useState } from 'react';
import { initialModel, type FeedComposerModel } from './model';
import type { ComposerMsg } from './messages';
import { update, type ComposerEffect } from './update';
import { runEffect, type ComposerEffectContext, type ComposerDependencies } from './effects';

// =============================================================================
// CORRECTION02j-rev2 — author-load lifecycle probe (rev1 → rev2 added
// observability on both sides of the abort guards; see
// docs/epics/feed-composer-api-wiring01/wal.md CORRECTION02j-rev2 for the
// full rev1 → rev2 diff rationale and the A1/A2/A3a/A3b/A4/HEALTHY_LOAD/
// REDUCER_DISPATCH classification table).
//
// R2 fired decisively in CI: the reducer entry sees status=loading while the
// DOM shows an enabled composer (textarea contains content, submitDisabled=false).
// The earlier (now retracted) CORRECTION02j instance-id probe attempted to
// classify the contradiction into SAME_INSTANCE_RESET vs DIFFERENT_INSTANCE,
// but:
//
//   - The framing was based on a false dichotomy. The contradiction is also
//     fully consistent with STILL_LOADING: same reducer instance, no reset,
//     no remount, AuthorLoaded never arrived, and the view never checks
//     status===form when deciding whether the button is enabled.
//
//   - The instance-id probe used a module-global counter incremented in render.
//     Under React 18 StrictMode the component body (and ref initialization) is
//     invoked twice per mount in development. React documents this exact
//     failure mode for global-counter-based identity. The probe could
//     spuriously emit id=1 then id=2 for a single real mount, making
//     "two ids => remount" unsafe to infer.
//
// This revision removes the instance-id probe and replaces it with markers
// around the missing author-load lifecycle:
//
//   Started
//     ↓
//   LoadAuthor effect emitted     (reducer marker already logs this)
//     ↓
//   runEffect entered + entry signal state
//                                  (marker #7: [composer-load-author-enter] aborted=...)
//     ↓
//   loadAuthor() resolved / err
//                                  (marker #8: [composer-load-author-settled] kind=...)
//     ↓
//   signal state at settle time   (encoded in marker #8)
//     ↓
//   stop at entry-abort           (marker #8a: [composer-load-author-stop] stage=entry-aborted)
//   stop at post-resolve-abort    (marker #8b: [composer-load-author-stop] stage=post-resolve-aborted)
//   stop at post-reject-abort     (marker #8c: [composer-load-author-stop] stage=post-reject-aborted)
//     ↓
//   AuthorLoaded dispatched       (marker #9: [composer-authorloaded-dispatch])
//     ↓
//   reducer loading -> form       (reducer marker #5 already logs this)
//
// The settled marker is emitted BEFORE the post-resolve abort guard so
// that "promise hung" (no settled marker) is distinguishable from
// "promise resolved/rejected but already aborted at continuation time"
// (settled marker present, stop marker present, no dispatch).
//
// The healthy transition is reproducible in JSDOM but did NOT happen in CI.
// This probe isolates which transition failed. No logic change. No state
// shape change. No spec-file change. To be removed once the transition is
// classified by empirical evidence.
// =============================================================================

// =============================================================================
// Hook props
// =============================================================================

/**
 * Props for the feed composer program hook.
 * CORRECTION01: Dependencies are injected from the composition root (R4).
 */
export interface UseFeedComposerProgramProps {
  /** Injected dependencies — production or test adapters */
  readonly dependencies: ComposerDependencies;
}

// =============================================================================
// Hook result type
// =============================================================================

/**
 * Result of the program hook — exposes model and action callbacks.
 */
export interface UseFeedComposerProgramResult {
  /** Current model state */
  readonly model: FeedComposerModel;
  /** Start the composer — loads author profile */
  readonly start: () => void;
  /** User changed the post content */
  readonly changeContent: (content: string) => void;
  /** User toggled the anonymous switch */
  readonly toggleAnonymous: (anonymous: boolean) => void;
  /** User clicked Publish */
  readonly publish: () => void;
  /** User clicked Retry from error state */
  readonly retry: () => void;
  /** User clicked Cancel */
  readonly cancel: () => void;
}

// =============================================================================
// Main program hook
// =============================================================================

/**
 * The main program hook — orchestrates state and effects.
 *
 * The reducer (update.ts) is PURE — it only returns new model + effects.
 * Effects are NOT run inside the reducer.
 *
 * CORRECTION02k: synchronous effect ownership.
 *
 * Effects are drained SYNCHRONOUSLY inside the dispatch wrapper, not
 * via a useEffect keyed on a state.pendingEffects queue. The previous
 * useEffect-drain design had a defect class: when two dispatches
 * happen in the same React batch (e.g. Started + ContentChanged in
 * one tick), the second reducer call overwrites pendingEffects and
 * React only commits the final state, so the earlier emitted effect
 * is silently dropped. This was the CI evidence class
 * EFFECT_EMITTED_BUT_NOT_DRAINED (WAL: 0fd7ca4a).
 *
 * In the synchronous-drain model:
 *   1. dispatch(msg) runs update(modelRef.current, msg) immediately.
 *   2. The reducer returns { model, effects }. There is no state slot
 *      for effects — effects are an OUTPUT of dispatch, not a piece
 *      of renderable state.
 *   3. The wrapper commits the new model via setModel and then
 *      synchronously calls runEffect for every effect the reducer
 *      produced. By the time dispatch returns, every emitted effect
 *      is already owned by its own per-occurrence AbortController.
 *   4. A subsequent dispatch (including one that overwrites the
 *      model entirely) cannot retroactively un-own an effect that
 *      was already drained.
 *
 * Dedup authority: the reducer itself.
 *   - Started is idempotent (status===idle guard in update.ts:90).
 *   - PublishClicked is idempotent within a submission
 *     (activeSubmissionId guard in update.ts:151).
 *   - All other transitions emit no effects.
 *   The reducer is the sole authority for whether a logical
 *   occurrence exists. The hook does NOT maintain a fingerprint set,
 *   a global dedup registry, or any payload-based identity (ACT
 *   §9.6 prohibition). The dedup question is answered entirely
 *   inside the pure reducer.
 *
 * Cancellation ownership: each emitted effect receives its own
 * AbortController, registered with a per-instance Set tracked in a
 * ref. On unmount, the cleanup useEffect aborts every controller in
 * the set. ER-7 (AbortSignal propagates to the port, no retry after
 * unmount) is satisfied because each effect's promise is the
 * continuation of a still-running runEffect whose signal was just
 * aborted.
 *
 * Effect-completion dispatches: when runEffect resolves and calls
 * ctx.dispatch(AuthorLoaded|...), the same dispatch wrapper handles
 * them. There is exactly one dispatch path for the whole program
 * (ACT §11), so all paths have identical ownership semantics.
 *
 * Usage:
 * ```tsx
 * function FeedComposePage() {
 *   const { model, start, changeContent, toggleAnonymous, publish, retry, cancel } =
 *     useFeedComposerProgram({ dependencies: composerDeps });
 *
 *   useEffect(() => { start(); }, [start]);
 *
 *   return (
 *     <FeedComposerView
 *       model={model}
 *       onContentChange={changeContent}
 *       onAnonymousToggle={toggleAnonymous}
 *       onPublish={publish}
 *       onRetry={retry}
 *       onCancel={cancel}
 *     />
 *   );
 * }
 * ```
 *
 * @param props - Hook props including injected dependencies
 */
export function useFeedComposerProgram(
  props: UseFeedComposerProgramProps
): UseFeedComposerProgramResult {
  const { dependencies } = props;

  // State: just the model. Effects are NOT part of renderable state;
  // they are synchronous side outputs of dispatch.
  const [model, setModel] = useState<FeedComposerModel>(initialModel);

  // modelRef: latest model for synchronous dispatch closure. Every
  // dispatch reads from modelRef.current so the dispatch wrapper
  // itself can stay referentially stable across renders.
  //
  // Per CORRECTION02k-CORRECTION01 (P1-01 review): modelRef.current
  // is mutated ONLY inside dispatch (after the reducer runs). React
  // state `model` is the render projection of modelRef; no
  // ref mutation occurs during render. The dispatch path is the
  // sole ref writer; this avoids the render-time useRef mutation
  // that React 18 StrictMode documents as a purity violation
  // (see react.dev/reference/react/useRef and StrictMode).
  const modelRef = useRef<FeedComposerModel>(initialModel);

  // controllersRef: per-instance set of AbortControllers owned by
  // currently in-flight effects. The unmount cleanup iterates and
  // aborts every one. This is the cancellation-ownership surface.
  const controllersRef = useRef<Set<AbortController>>(new Set());

  // ownedRef: per-instance ref-backed registry of in-flight LOGICAL
  // effect occurrences (CORRECTION02k-CORRECTION02 LIVENESS).
  //
  // Each entry maps a stable logical key to the OwnedOccurrence
  // record for that occurrence. The record holds the effect
  // payload (preserved across dep changes) and the current
  // physical controller (replaced on each dep change).
  //
  // This is NOT React state and is NOT state.pendingEffects. It
  // is mutated only from event-time / effect-time code (dispatch
  // wrapper, dep-change setup useEffect, and per-controller
  // .finally finalizers). React render bodies never touch it.
  type OwnedOccurrence = {
    readonly effect: ComposerEffect;
    controller: AbortController;
  };
  const ownedRef = useRef<Map<string, OwnedOccurrence>>(new Map());

  // keyOf: stable logical-occurrence key. The key is a function of
  // the effect payload only — not of the controller, not of the
  // dependency adapter. Two physical executions of the SAME
  // logical occurrence share the same key, which lets the
  // dep-change setup find and re-launch them.
  const keyOf = (effect: ComposerEffect): string => {
    if (effect.type === 'LoadAuthor') return 'LoadAuthor';
    // SubmitPost is keyed by submissionId. The reducer guarantees
    // a unique submissionId per logical occurrence (nextSubmissionId
    // counter). A new submissionId therefore always means a new
    // logical occurrence that should NOT be merged with any prior
    // occurrence.
    return `SubmitPost:${String(effect.submissionId)}`;
  };

  // Stable dispatch wrapper. Drains effects synchronously inside the
  // call frame so an emitted effect acquires delivery ownership
  // BEFORE any later dispatch can interfere with renderable state.
  // Reducer (update.ts) is invoked here, but reducer remains pure:
  // it returns new model + effects without performing side effects.
  const dispatch = useCallback(
    (msg: ComposerMsg): void => {
      // Pure reducer call. No I/O, no port, no browser API.
      const result = update(modelRef.current, msg);

      // CORRECTION02i — direct-boundary probe marker #5 (post-update).
      // Logs the post-update model status and the effect types the
      // reducer produced (or 'none'). Console-log only — no behavior
      // change. To be removed once classified.
      // eslint-disable-next-line no-console
      console.log(
        `[composer-reducer-result] ` +
          `msg=${msg.type} ` +
          `before=${modelRef.current.status} ` +
          `after=${result.model.status} ` +
          `effects=${result.effects.map((effect) => effect.type).join(',') || 'none'}`
      );

      // Commit new model. Update modelRef BEFORE setModel so that any
      // reentrant dispatch (e.g. a synchronous effect-completion
      // dispatch from a then-able inside this same dispatch frame)
      // observes the post-update model.
      modelRef.current = result.model;
      setModel(result.model);

      // CORRECTION02k: synchronous effect drain.
      //
      // Every effect emitted by this reducer call is now owned by
      // its own per-occurrence AbortController. A subsequent
      // dispatch (in this same tick or later) cannot retroactively
      // delete an effect that is already running.
      //
      // The reducer is the dedup authority (see file header). We do
      // NOT add a fingerprint Set here — the reducer has already
      // decided whether the effect is a real logical occurrence.
      //
      // CORRECTION02k-CORRECTION02 LIVENESS: each emitted effect is
      // also registered in `ownedRef` under its stable logical key.
      // This makes the in-flight occurrence findable by the
      // dep-change setup useEffect, which aborts the OLD physical
      // controller and re-launches the SAME logical occurrence
      // against the new dependencies.
      for (const effect of result.effects) {
        const controller = new AbortController();
        controllersRef.current.add(controller);
        const key = keyOf(effect);
        ownedRef.current.set(key, { effect, controller });
        const ctx: ComposerEffectContext = { dispatch };
        // void runEffect: the promise is fire-and-forget from the
        // dispatcher's perspective. The .finally:
        //   - always removes THIS controller from the per-instance
        //     set (so the unmount cleanup doesn't abort
        //     already-finished work).
        //   - only deletes the registry entry if THIS controller
        //     is still the CURRENT controller for that key.
        //     If a dep change already replaced this controller,
        //     the registry entry belongs to the replacement, and
        //     we must NOT delete it. (CORRECTION02k-CORRECTION02
        //     stale-finalizer safety.)
        void runEffect(effect, ctx, dependencies, controller.signal).finally(
          () => {
            controllersRef.current.delete(controller);
            const current = ownedRef.current.get(key);
            if (current !== undefined && current.controller === controller) {
              ownedRef.current.delete(key);
            }
          }
        );
      }
    },
    [dependencies]
  );

  // Cancellation ownership + LIVENESS re-issue (CORRECTION02k-
  // CORRECTION02). Two useEffects share `[dependencies]` and run
  // in declaration order:
  //
  //   1. cleanup-effect (below): on dep-change or unmount, abort
  //      every in-flight controller. This is defense in depth —
  //      the OLD runEffect will see signal.aborted=true at its
  //      next await checkpoint, and effects.ts post-resolve /
  //      post-reject guards suppress any stale dispatch.
  //
  //   2. setup-effect (further below): on dep-change (NOT on
  //      unmount — unmount only runs cleanup, no setup), iterate
  //      the registry of OwnedOccurrences and re-launch each
  //      in-flight logical occurrence against the NEW
  //      dependencies with a fresh AbortController. The new
  //      controller atomically replaces the old one in the
  //      registry entry.
  //
  // Why two effects rather than one: React guarantees cleanup
  // from the previous render runs before the new setup, and
  // pairs are processed in declaration order. Splitting
  // cleanup-and-relaunch across two effects preserves the
  // invariant that every OLD controller is aborted BEFORE any
  // NEW controller is launched. (Inside a single useEffect, the
  // setup body would see the new `dependencies` closure but the
  // cleanup body would also have access to the same closure —
  // so a single effect would still work — but the two-effect
  // pattern keeps each concern isolated and matches React's
  // setup/cleanup mental model.)
  //
  // The OLD useReducer+useEffect design keyed on
  // `[state.pendingEffects, stableDependencies]`. CORRECTION02k
  // removed `state.pendingEffects` from React state entirely;
  // LIVENESS replaces it with a useRef-backed registry that is
  // NOT React state and is NOT mutated during render.
  //
  // Caller contract: the `dependencies` reference must be stable
  // across renders that don't change the actual adapter.
  // FeedComposePage meets this via
  //   useMemo<ComposerDependencies>(
  //     () => buildProductionComposerDependencies(registrationKind),
  //     [registrationKind]
  //   )

  // (1) Cleanup effect — abort every in-flight controller on
  // dep-change or unmount.
  useEffect(() => {
    return () => {
      const controllers = controllersRef.current;
      controllersRef.current = new Set();
      for (const controller of controllers) {
        controller.abort();
      }
    };
  }, [dependencies]);

  // (2) Re-issue effect — for each OwnedOccurrence still in the
  // registry, replace its controller with a fresh one and
  // re-launch runEffect against the new dependencies. Runs on
  // dep-change AND on first mount, but is a no-op on first mount
  // because ownedRef is empty.
  //
  // React's effect ordering: setup-1 of effect (1) ran on
  // mount (no-op); on dep-change, cleanup of (1) runs first
  // (aborting all current controllers), then setup of (2) runs
  // (re-launching each occurrence against new deps).
  useEffect(() => {
    // Snapshot: don't mutate while iterating (we replace entries
    // below; iterating a live Map while mutating is implementation-
    // defined).
    const snapshot = Array.from(ownedRef.current.entries());
    for (const [key, rec] of snapshot) {
      // Defense: if the record was already replaced or removed
      // between snapshot and now (shouldn't happen because no
      // user dispatch can run between the previous cleanup and
      // this setup, but be safe), skip.
      const stillTracked = ownedRef.current.get(key);
      if (stillTracked !== rec) continue;

      const newController = new AbortController();
      controllersRef.current.add(newController);
      // Atomically replace the record's controller. The old
      // controller is now orphaned — it will settle via its
      // already-aborted signal and its .finally will see that
      // `current.controller !== oldController` and leave the
      // registry entry alone.
      rec.controller = newController;

      const ctx: ComposerEffectContext = { dispatch };
      void runEffect(rec.effect, ctx, dependencies, newController.signal).finally(
        () => {
          controllersRef.current.delete(newController);
          const current = ownedRef.current.get(key);
          if (current !== undefined && current.controller === newController) {
            ownedRef.current.delete(key);
          }
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dependencies]);

  // --- Action callbacks ---
  //
  // All callbacks route through the single dispatch wrapper. There
  // is exactly one dispatch path for the whole program (ACT §11).

  const start = useCallback(() => {
    // CORRECTION02i — direct-boundary probe marker #4 (pre-reducer).
    // Logs every dispatched message type and the status the reducer
    // observes at entry. Console-log only — no behavior change.
    // eslint-disable-next-line no-console
    console.log(`[composer-reducer-enter] msg=Started status=${modelRef.current.status}`);
    dispatch({ type: 'Started' });
  }, [dispatch]);

  const changeContent = useCallback(
    (content: string) => {
      // eslint-disable-next-line no-console
      console.log(`[composer-reducer-enter] msg=ContentChanged status=${modelRef.current.status}`);
      dispatch({ type: 'ContentChanged', content });
    },
    [dispatch]
  );

  const toggleAnonymous = useCallback(
    (anonymous: boolean) => {
      // eslint-disable-next-line no-console
      console.log(
        `[composer-reducer-enter] msg=AnonymousToggled status=${modelRef.current.status}`
      );
      dispatch({ type: 'AnonymousToggled', anonymous });
    },
    [dispatch]
  );

  const publish = useCallback(() => {
    // CORRECTION02h — direct-boundary probe marker #3.
    // First statement of the publish() callback. Proves that the
    // React-bound `publish` reference actually entered when
    // onPublish() is invoked from view.tsx. Console-log only —
    // no behavior change. To be removed once the publish-enter
    // evidence is in.
    // eslint-disable-next-line no-console
    console.log('[composer-publish-enter]');
    // eslint-disable-next-line no-console
    console.log(`[composer-reducer-enter] msg=PublishClicked status=${modelRef.current.status}`);
    dispatch({ type: 'PublishClicked' });
  }, [dispatch]);

  const retry = useCallback(() => {
    // eslint-disable-next-line no-console
    console.log(`[composer-reducer-enter] msg=RetryClicked status=${modelRef.current.status}`);
    dispatch({ type: 'RetryClicked' });
  }, [dispatch]);

  const cancel = useCallback(() => {
    // eslint-disable-next-line no-console
    console.log(`[composer-reducer-enter] msg=CancelClicked status=${modelRef.current.status}`);
    dispatch({ type: 'CancelClicked' });
  }, [dispatch]);

  return {
    model,
    start,
    changeContent,
    toggleAnonymous,
    publish,
    retry,
    cancel,
  };
}
